import {
  FlyoutMenu,
  useTopNavBarSelector,
  TopNavBarSelector,
  Typography,
  useModal,
  colors,
  Stack,
} from '@ui-components/neo.tax';
import { ReactComponent as Calendar } from '@ui-components/neo.tax/dist/icons/calendar.svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FreeFocusInside } from 'react-focus-lock';

import { ProductType } from '@neo.tax/shared/graphql';
import {
  useCompany,
  useCurrentCompanyIdOptional,
  useIsMobile,
  useUserFromAuth,
  useCurrentUser,
  useSelectedProductOptional,
  useFiscalYear,
  useSelectedPeriodNameOptional,
  useSelectedFiscalYearIdOptional,
} from '@neo.tax/shared/hooks';
import { reactiveVarOrchestrator } from '@neo.tax/shared/orchestrators';
import { useLaunchDarklyIdentifyCompany } from '@neo.tax/shared/third-parties';
import {
  getDefaultAscPath,
  getDefaultRdPath,
  getFiscalYearSubsetPeriod,
  periodDisplayName,
  productDisplayName,
} from '@neo.tax/shared/utils';

import { FiscalPeriodModal } from '../fiscal-period-modal';
import { HelpMenu } from '../help-menu';
import { useNavigateTo } from '../navigate';
import { NotificationMenu } from '../notification-menu';
import { OrganizationDashboard } from '../organization-dashboard';
import { ProfileMenu } from '../profile-menu';
import { useDefaultProductAndPeriod, useOrganizationDashboardNavIds } from './hooks';
import {
  SuperUserModeText,
  CompanyNameContainer,
  NavColumnContainer,
  NavContainer,
  NavInnerContainer,
  PeriodPickerContainer,
  StyledAvatarWithText,
  StyledWordmark,
  VerticalDivider,
  OrganizationLogo,
} from './organization-dashboard-nav.styled';

export const OrganizationDashboardNav = (): React.ReactElement => {
  const companyId = useCurrentCompanyIdOptional();
  const productFromUrl = useSelectedProductOptional();
  const fiscalYearIdFromUrl = useSelectedFiscalYearIdOptional();
  const periodNameFromUrl = useSelectedPeriodNameOptional();
  const { fiscalYear } = useFiscalYear({ fiscalYearId: fiscalYearIdFromUrl });
  const { company } = useCompany({ companyId });
  const { user } = useCurrentUser();
  const { organization } = user ?? {};
  const navigate = useNavigateTo();
  const [defaultNavigationCompleted, setDefaultNavigationCompleted] = useState(false);

  // We identify the launch darkly company here since this is the earliest point a company has been selected
  const loadingLaunchDarklyIdentifyCompany = useLaunchDarklyIdentifyCompany(company);
  // We can't load the default period until we have targeted the company
  const {
    fiscalYearId: defaultFiscalYearId,
    loading: loadingDefaultProductAndPeriod,
    periodName: defaultPeriodName,
    product: defaultProduct,
    updateProductPeriodFromLocalStorage,
  } = useDefaultProductAndPeriod(loadingLaunchDarklyIdentifyCompany);

  const {
    isOpen: isOrganizationDashboardOpen,
    setIsOpen: setIsOrganizationDashboardOpen,
    toggle: toggleOrganizationDashboard,
  } = useTopNavBarSelector({ initialIsOpen: !companyId });
  const { isOpen: isFiscalPeriodModalOpen, setIsOpen: setIsFiscalPeriodModalOpen } = useModal();

  const {
    logoId,
    organizationNavBarButtonId: companyNavBarButtonId,
    organizationNavPeriodPickerId: companyNavPeriodPickerId,
  } = useOrganizationDashboardNavIds();

  const { isSuperUserMode } = useUserFromAuth();
  const { isMobile } = useIsMobile();

  const period = useMemo(() => {
    if (!fiscalYear?.taxYearStartDate || !fiscalYear?.taxYearEndDate || !periodNameFromUrl) {
      return null;
    }
    return getFiscalYearSubsetPeriod(fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodNameFromUrl);
  }, [fiscalYear?.taxYearStartDate, fiscalYear?.taxYearEndDate, periodNameFromUrl]);

  // Handle navigation based on the default product and period configuration
  // navigate to the corresponding path if the current path is not the default path
  useEffect(() => {
    if (loadingDefaultProductAndPeriod || defaultNavigationCompleted) {
      return;
    }
    if (!companyId) {
      return;
    }
    setDefaultNavigationCompleted(true);
    if (defaultProduct && defaultPeriodName && defaultFiscalYearId) {
      // Only navigate if the defaults are different from the current URL values
      const isDifferent =
        defaultProduct !== productFromUrl ||
        defaultPeriodName !== periodNameFromUrl ||
        defaultFiscalYearId !== fiscalYearIdFromUrl;

      if (isDifferent) {
        if (defaultProduct === ProductType.UsRd) {
          const path = getDefaultRdPath({ companyId, fiscalYearId: defaultFiscalYearId });
          navigate(path);
        } else {
          const path = getDefaultAscPath({
            companyId,
            fiscalYearId: defaultFiscalYearId,
            periodName: defaultPeriodName,
          });
          navigate(path);
        }
      }
    }
  }, [
    companyId,
    defaultFiscalYearId,
    defaultNavigationCompleted,
    defaultPeriodName,
    defaultProduct,
    fiscalYearIdFromUrl,
    loadingDefaultProductAndPeriod,
    navigate,
    periodNameFromUrl,
    productFromUrl,
  ]);

  // Once the default navigation is completed,
  // 1. Open company selector if no company selected
  // 2. Open fiscal period modal if product or period are not in the url

  // Tracks whether this effect is the one that opened the fiscal period modal, so a
  // later run only closes it if it was auto-opened here, never a modal the user opened themselves.
  const autoOpenedFiscalPeriodModalRef = useRef(false);

  useEffect(() => {
    if (!defaultNavigationCompleted) {
      return;
    }
    if (!companyId) {
      // No company selected - open company selector
      setIsOrganizationDashboardOpen(true);
      setIsFiscalPeriodModalOpen(false);
      autoOpenedFiscalPeriodModalRef.current = false;
    } else {
      // clear reactive var when company changed
      reactiveVarOrchestrator.clearReactiveVars();
      setIsOrganizationDashboardOpen(false);

      if (!productFromUrl || !fiscalYearIdFromUrl || !periodNameFromUrl) {
        // Product and period are not in the url - open fiscal period modal, unless it's
        // already open, in which case this effect didn't open it and shouldn't claim it did.
        if (!isFiscalPeriodModalOpen) {
          setIsFiscalPeriodModalOpen(true);
          autoOpenedFiscalPeriodModalRef.current = true;
        }
      } else if (autoOpenedFiscalPeriodModalRef.current) {
        setIsFiscalPeriodModalOpen(false);
        autoOpenedFiscalPeriodModalRef.current = false;
      }
    }
  }, [
    companyId,
    defaultNavigationCompleted,
    fiscalYearIdFromUrl,
    isFiscalPeriodModalOpen,
    periodNameFromUrl,
    productFromUrl,
    setIsFiscalPeriodModalOpen,
    setIsOrganizationDashboardOpen,
  ]);

  return (
    <FreeFocusInside>
      <NavContainer>
        <FlyoutMenu>
          {({ FlyoutContent }): JSX.Element => (
            <>
              <NavInnerContainer $superUserMode={isSuperUserMode} boxShadow="sm" alignItems="center">
                {isSuperUserMode && (
                  <SuperUserModeText as="p" fontSize="xl" fontWeight="semibold" lineHeight={4}>
                    super user mode
                  </SuperUserModeText>
                )}
                <NavColumnContainer alignItems="center">
                  <a id={logoId} href="https://neo.tax" target="_blank" rel="noopener noreferrer">
                    <StyledWordmark />
                  </a>
                  {organization?.photoUrl && (
                    <>
                      <VerticalDivider />
                      <OrganizationLogo src={organization.photoUrl} alt="Organization Logo" />
                    </>
                  )}
                </NavColumnContainer>

                {!isMobile && (
                  <CompanyNameContainer justifyContent="center" gap={2} alignItems="center">
                    <TopNavBarSelector
                      id={companyNavBarButtonId}
                      disabled={!companyId}
                      onClick={toggleOrganizationDashboard}
                      open={isOrganizationDashboardOpen}
                    >
                      {companyId ? (
                        <StyledAvatarWithText
                          size={6}
                          fontWeight="medium"
                          fontSize="sm"
                          title={company?.shortName}
                          placeholder="Loading..."
                          variant="circular"
                        />
                      ) : (
                        <Typography as="span" fontSize="sm">
                          No Company Selected
                        </Typography>
                      )}
                    </TopNavBarSelector>
                    <TopNavBarSelector
                      id={companyNavPeriodPickerId}
                      disabled={!companyId || isOrganizationDashboardOpen}
                      onClick={(): void => setIsFiscalPeriodModalOpen(true)}
                      open={false}
                    >
                      <PeriodPickerContainer alignItems="center" gap={2}>
                        <Calendar width={16} color={colors.gray[500]} />
                        <Stack direction="row" gap={1.5} alignItems="center">
                          <Typography as="span" fontSize="sm" fontWeight="medium" color={colors.gray[700]}>
                            {productFromUrl ? productDisplayName(productFromUrl) : '----'}
                          </Typography>
                          <Typography as="span" fontSize="xs" color={colors.gray[400]}>
                            •
                          </Typography>
                          <Typography as="span" fontSize="sm" fontWeight="normal" color={colors.gray[500]}>
                            {period ? periodDisplayName(period) : '----'}
                          </Typography>
                        </Stack>
                      </PeriodPickerContainer>
                    </TopNavBarSelector>
                  </CompanyNameContainer>
                )}

                <NavColumnContainer alignItems="center" justifyContent="end" gap={3}>
                  <HelpMenu />
                  {companyId && <NotificationMenu />}
                  <ProfileMenu />
                </NavColumnContainer>
              </NavInnerContainer>
              <FlyoutContent open={isOrganizationDashboardOpen}>
                <OrganizationDashboard />
              </FlyoutContent>
            </>
          )}
        </FlyoutMenu>
      </NavContainer>
      {companyId && (
        <FiscalPeriodModal
          companyId={companyId}
          selectedProduct={productFromUrl}
          isOpen={isFiscalPeriodModalOpen}
          defaultProduct={defaultProduct}
          defaultFiscalYearId={defaultFiscalYearId}
          setIsOpen={setIsFiscalPeriodModalOpen}
          onProductPeriodChange={updateProductPeriodFromLocalStorage}
        />
      )}
    </FreeFocusInside>
  );
};
